export type ChartType = 'bar' | 'grouped_bar' | 'pie';
export type ColorScheme = 'brand' | 'platform' | 'categorical';
export type Platform = 'instagram' | 'youtube' | 'tiktok' | 'none';

export interface ChartDataPoint {
  label: string;
  value: number;
  platform?: Platform;
}

/**
 * Keywords that indicate categorical demographic data.
 * Used to detect when data represents categories rather than platforms or time-series.
 */
const CATEGORICAL_KEYWORDS = [
  'male',
  'female',
  'jakarta',
  'bekasi',
  'city',
  'gender'
] as const;

export const COLOR_PALETTES = {
  brand: {
    primary: '#a78bfa',      // purple-400
    secondary: '#c4b5fd',    // purple-300
    gradient: 'linear-gradient(90deg, #a78bfa, #c4b5fd)'
  },
  platform: {
    instagram: '#a78bfa',    // Purple (matches brand)
    youtube: '#ef4444',      // Red (YouTube brand)
    tiktok: '#06b6d4',       // Cyan (TikTok brand)
    none: '#9ca3af'          // Neutral gray
  } as Record<Platform, string>,
  categorical: [
    '#b4a5fa',  // Soft purple (desaturated from #a78bfa)
    '#f491c4',  // Soft pink (desaturated from #ec4899)
    '#7dd3f0',  // Soft cyan (desaturated from #06b6d4)
    '#fcc577',  // Soft amber (desaturated from #f59e0b)
    '#9ca3af'   // Soft gray (desaturated from #6b7280, used for "Others")
  ]
};

/**
 * Detects appropriate color scheme based on data structure.
 *
 * Logic:
 * - Multi-platform (2+ unique platforms) → "platform"
 * - Single platform with platform field → "brand"
 * - No platform field but categorical labels (gender, cities) → "categorical"
 * - Default → "brand"
 */
export function detectColorScheme(data: ChartDataPoint[]): ColorScheme {
  // Check if any data point has platform field
  const hasPlatform = data.some(d => d.platform !== undefined);

  if (hasPlatform) {
    const uniquePlatforms = [...new Set(
      data.map(d => d.platform).filter((p): p is Platform => p !== undefined)
    )];

    // Multiple platforms → use platform-specific colors
    // Single platform with platform tag → still use brand color
    return uniquePlatforms.length > 1 ? 'platform' : 'brand';
  }

  // Check if data looks like demographic categories
  const labels = data.map(d => d.label.toLowerCase());
  const isCategorical = labels.some(label =>
    CATEGORICAL_KEYWORDS.some(keyword => label.includes(keyword))
  );

  return isCategorical ? 'categorical' : 'brand';
}

/**
 * Parses JSON string into ChartDataPoint array.
 * Filters out invalid items (missing label or value).
 * Returns empty array if JSON is malformed.
 */
export function parseChartData(jsonString: string): ChartDataPoint[] {
  if (!jsonString || jsonString.trim() === '') {
    return [];
  }

  try {
    const parsed = JSON.parse(jsonString);

    if (!Array.isArray(parsed)) {
      console.warn('Chart data is not an array:', parsed);
      return [];
    }

    // Filter out items missing required fields
    return parsed.filter(
      (item): item is ChartDataPoint =>
        typeof item === 'object' &&
        item !== null &&
        typeof item.label === 'string' &&
        typeof item.value === 'number'
    );
  } catch (error) {
    console.error('Failed to parse chart data:', error);
    return [];
  }
}

/**
 * Gets color for a specific data point based on color scheme.
 */
export function getDataPointColor(
  index: number,
  colorScheme: ColorScheme,
  platform?: Platform
): string {
  switch (colorScheme) {
    case 'platform':
      return platform ? COLOR_PALETTES.platform[platform] : COLOR_PALETTES.categorical[0];
    case 'categorical':
      return COLOR_PALETTES.categorical[index % COLOR_PALETTES.categorical.length];
    case 'brand':
    default:
      return COLOR_PALETTES.brand.primary;
  }
}
